import React, { useEffect, useState } from "react";
import { Icon, Keys } from "../numberSenseUi";

/* ============================================================
   Number Sense — home. Kept focused on the course: who you are →
   what to do now (continue + daily) → your track + the full lesson
   path. Everything else (drills, the band room, settings) lives on
   the "practice & play" hub, reached from the menu button, so the
   home screen stays about learning and the paths stay visible.

   `homeUnits` is the derived path (units → nodes with cls/icon/
   unlocked/label) the page computes; this component renders it and
   calls back on taps.
   ============================================================ */

// Small inline "what should we call you?" control on the hero.
const NameBar = ({ name, onSave }) => {
  const [value, setValue] = useState(name || "");
  useEffect(() => {
    setValue(name || "");
  }, [name]);
  const dirty = value.trim() && value.trim() !== (name || "");
  return (
    <div className="namebar">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && dirty) onSave(value.trim());
        }}
        placeholder="what should we call you?"
      />
      <button disabled={!dirty} onClick={() => onSave(value.trim())}>
        save
      </button>
    </div>
  );
};

// A little SVG ring showing progress toward the daily XP goal.
const GoalRing = ({ pct, earned, goal }) => {
  const r = 18;
  const circ = 2 * Math.PI * r;
  return (
    <div className="goal-ring" aria-label={`daily goal: ${earned} of ${goal} XP`}>
      <svg viewBox="0 0 44 44">
        <circle className="track" cx="22" cy="22" r={r} />
        <circle
          className="fill"
          cx="22"
          cy="22"
          r={r}
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - Math.min(1, pct / 100))}
          transform="rotate(-90 22 22)"
        />
      </svg>
      <div className="goal-label">
        <b>{earned}</b>
        <small>/{goal}</small>
      </div>
    </div>
  );
};

const HomeScreen = ({
  progress,
  homeUnits,
  gameData,
  doneLessons,
  totalLessons,
  tracks = [],
  activeTrackId,
  onSelectTrack,
  dailyDone,
  dailyGoal,
  reviewDue = 0,
  onDaily,
  onStartNode,
  onLearnUnit,
  onReview,
  onOpenMenu,
  onSaveName,
}) => {
  // The lesson to resume: the first incomplete unlocked node (it carries the
  // "pulse" class). Null when the active track is fully complete.
  let resume = null;
  for (const u of homeUnits) {
    const node = u.nodes.find((n) => (n.cls || "").includes("pulse"));
    if (node) {
      resume = { ...node, unitTitle: u.unit.title };
      break;
    }
  }
  const activeTrack = tracks.find((t) => t.id === activeTrackId);
  const canReview = gameData.pairs.length >= 3;

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="topbar">
          <div className="brandmark">
            <b>number sense</b>
            <small>
              {progress.name ? `hey, ${progress.name}` : "play by the numbers"}
            </small>
          </div>
          <div className="stat streak">
            <Icon name="flame" />
            {progress.streak}
          </div>
          <div className="stat xp">
            <Icon name="bolt" />
            {progress.xp}
          </div>
          <button className="iconbtn topbar-menu" onClick={onOpenMenu} aria-label="practice & play menu">
            <Icon name="grid" />
          </button>
        </div>
        <div className="scroll">
          <div className="hero">
            <h1>
              stop memorizing chords.
              <br />
              start hearing numbers.
            </h1>
            <p>
              The Nashville Number System in your hands: I–IV–V–vi, the same shapes
              in every key, drilled like a game. Your progress saves automatically.
            </p>
            <a className="ns-xref" href="/keyboard">
              new to this? read the full lesson plan →
            </a>
            <a className="ns-xref" href="/number-sense-v2">
              or try v2: every scale type, drilled in any key →
            </a>
            <NameBar name={progress.name} onSave={onSaveName} />
          </div>

          {/* what to do now — continue leads, daily is the secondary option */}
          {resume ? (
            <button
              className="continue-card"
              onClick={() => onStartNode(resume)}
            >
              <div className="ic">
                <Icon name="play" />
              </div>
              <div className="txt">
                <div className="kicker">continue</div>
                <div className="ttl">{resume.unitTitle}</div>
                <div className="sub">
                  {resume.label === "start" ? "start the first lesson" : resume.label}
                  {activeTrack ? ` · ${activeTrack.title}` : ""}
                </div>
              </div>
              <span className="chev">›</span>
            </button>
          ) : (
            <button className="continue-card done" onClick={canReview ? onReview : onDaily}>
              <div className="ic">
                <Icon name="trophy" />
              </div>
              <div className="txt">
                <div className="kicker">nice work</div>
                <div className="ttl">
                  {activeTrack ? `${activeTrack.title} complete` : "track complete"}
                </div>
                <div className="sub">
                  {canReview ? "review your weak spots, or switch tracks" : "switch tracks to keep going"}
                </div>
              </div>
              <span className="chev">›</span>
            </button>
          )}

          <button
            className={`daily-card ${dailyDone ? "done" : ""}`}
            onClick={onDaily}
          >
            <div className="ic">
              <Icon name={dailyDone ? "check" : "today"} />
            </div>
            <div className="txt">
              <div className="ttl">
                daily challenge
                {dailyDone && <span className="daily-chip">done today</span>}
              </div>
              <div className="sub">
                {dailyDone
                  ? "nice — back tomorrow for a fresh set"
                  : "one mixed set, the same for everyone today"}
              </div>
            </div>
            <GoalRing
              pct={dailyGoal.pct}
              earned={dailyGoal.earned}
              goal={dailyGoal.goal}
            />
          </button>

          {/* stale concepts resurface here (and as amber ticks on the path) */}
          {reviewDue > 0 && canReview && (
            <button className="due-banner" onClick={onReview}>
              <Icon name="timer" />
              <span>
                <b>
                  {reviewDue} chord{reviewDue === 1 ? "" : "s"}
                </b>{" "}
                gone stale — a quick review brings {reviewDue === 1 ? "it" : "them"} back
              </span>
              <span className="chev">›</span>
            </button>
          )}

          {/* your course: pick a track, follow the path */}
          {tracks.length > 1 && (
            <div className="track-switch" role="tablist" aria-label="curriculum track">
              {tracks.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={t.id === activeTrackId}
                  className={`track-pill ${t.id === activeTrackId ? "on" : ""}`}
                  onClick={() => onSelectTrack(t.id)}
                >
                  <b>{t.title}</b>
                  <small>{t.gloss}</small>
                </button>
              ))}
            </div>
          )}

          {homeUnits.map(({ unit, ui, nodes, teachable, kind }) => (
            <div className={`unit${kind === "final" ? " unit-final" : ""}`} key={unit.id}>
              <div className="unit-head">
                <div>
                  <div className="ttl">{unit.title}</div>
                  <div className="sub">
                    {kind === "final" ? "the whole track" : `unit ${ui + 1}`}
                  </div>
                </div>
                {teachable ? (
                  <button
                    className="unit-learn"
                    onClick={() => onLearnUnit(ui)}
                    aria-label={`learn: ${unit.title}`}
                  >
                    <Icon name="book" />
                    learn
                  </button>
                ) : (
                  <div className="gloss">{unit.gloss}</div>
                )}
              </div>
              <div className="nodes">
                {nodes.map((n, idx) => (
                  <React.Fragment key={n.id}>
                    <div className="node-wrap">
                      <button
                        className={n.cls}
                        disabled={!n.unlocked}
                        onClick={() => n.unlocked && onStartNode(n)}
                      >
                        <Icon name={n.icon} />
                      </button>
                      <div className="node-label">{n.label}</div>
                    </div>
                    {idx < nodes.length - 1 && <div className="connector" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}

          <div className="footnote">
            <Keys />
            <br />
            {doneLessons}/{totalLessons} lessons · built on the /keyboard lesson plan
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeScreen;
